/**
 * Salvation Team overview (/app/salvation)
 *
 * KPI tiles for the journey (in follow-up, still to reach, spoken to, met at church, handed over, connected) and for
 * what needs a leader now (new decisions waiting for review, past the deadline, Backup responsible, needs review,
 * not assigned); each tile opens the filtered People list (or the review queue). Then the follow-up progress meter,
 * the status breakdown (donut), the campus comparison (bar chart and one row per campus team), the priorities (nobody
 * reached yet, nearest deadline first; a click opens the person drawer). A campus filter narrows everything; leaders
 * only see the campuses they lead. "Add a person" and "Bring in decisions" are the header actions.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconUsers,
  IconPhoneOff,
  IconPhone,
  IconBuildingChurch,
  IconTrendingUp,
  IconHeartHandshake,
  IconClockExclamation,
  IconArrowsExchange,
  IconEye,
  IconUserQuestion,
  IconPlus,
  IconHeart,
  IconInbox,
  IconRefresh,
  IconChevronRight,
  IconClock,
  IconDatabaseImport,
} from '@tabler/icons-react';
import { SALVATION_CONVERT_STATUS as S, useSalvationAdminService, type SalvationStatsView } from '@actschurch/ccms-sdk';
import { LayoutContainer } from '@/components/ui/layout';
import { Alert, Badge, Button, Grid, Group, Paper, ScrollArea, Select, SimpleGrid, Skeleton, Stack, Table, Text } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import { BarChartWidget, DonutChartWidget } from '@/components/advanced/Widgets';
import { FormsStatTile as StatTile, StatTilesSkeleton, SectionCard, InlineEmpty, MeterBar, MeterRow } from '../../FormsManagement/components/FormsUi';
import { useSalvationPermissions } from '@/hooks/salvation/useSalvationPermissions';
import { SalvationPageHeader, StatusBadge, PersonFlags } from '../components/SalvationUi';
import { AddPersonModal } from '../components/PersonModals';
import { PersonDrawer } from '../components/PersonDrawer';
import { useSalvationLookups, useSalvationTeams } from '../components/useSalvationData';
import { cssColour, plural, relative, salvationErrorMessage, salvationKeys, salvationPaths } from '../components/salvationUtils';

const PAGE = 'salvation.overview';
const CHART_H = 280;

function progressLabel(s: SalvationStatsView) {
  return `${s.progressPercent}% of ${plural(s.active, 'person', 'people')} in follow-up spoken to or met`;
}

function ChartPlaceholder({ title, subtitle, text }: { title: string; subtitle?: string; text: string }) {
  return (
    <SectionCard title={title} description={subtitle}>
      <InlineEmpty icon={<IconHeart size={22} />} title="Nothing to show yet" description={text} />
    </SectionCard>
  );
}

export default function SalvationOverviewPage() {
  const admin = useSalvationAdminService();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const perms = useSalvationPermissions();
  const teams = useSalvationTeams();
  const { statuses } = useSalvationLookups();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [openPerson, setOpenPerson] = useState<number | null>(null);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: salvationKeys.overview(teamId ? Number(teamId) : null),
    queryFn: () => admin.getOverview(teamId ? Number(teamId) : null),
    refetchInterval: 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: salvationKeys.overviewRoot() });
  const t = data?.totals;
  const goPeople = (params: Record<string, string>) => navigate(`${salvationPaths.people}?${new URLSearchParams({ ...(teamId ? { teamId } : {}), ...params })}`);

  const statusColour = (id: number, fallback: string) => cssColour(statuses.get(id)?.color, fallback);
  const breakdown = t
    ? [
        { name: 'Still to reach', value: t.stillToReach, color: statusColour(S.NotContacted, 'orange') },
        { name: 'Spoken to', value: t.spokenTo, color: statusColour(S.Reached, 'cyan') },
        { name: 'Met at church', value: t.met, color: statusColour(S.Met, 'green') },
        { name: 'Handed over', value: t.handedOver, color: statusColour(S.HandedOver, 'grape') },
        { name: 'Connected', value: t.connected, color: statusColour(S.Connected, 'teal') },
        { name: 'Unreachable or declined', value: t.declined, color: statusColour(S.Declined, 'gray') },
      ].filter((d) => d.value > 0)
    : [];
  const breakdownTotal = breakdown.reduce((n, d) => n + d.value, 0);
  const byCampus = (data?.teams ?? []).map((row) => ({
    campus: row.teamName.replace(/ Salvation Team$/i, ''),
    toReach: row.stats.stillToReach,
    spoken: row.stats.spokenTo + row.stats.met,
    handedOver: row.stats.handedOver + row.stats.connected,
  }));
  const showCampusChart = byCampus.length > 1 && byCampus.some((c) => c.toReach + c.spoken + c.handedOver > 0);

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <SalvationPageHeader
            title="Salvation Team"
            description="Every person matters. One Lead, one Backup, clear ownership and a personal welcome."
            helpKey={PAGE}
            actions={
              <>
                <ActionIcon variant="subtle" size="lg" onClick={refresh} loading={isFetching && !isLoading} aria-label="Refresh">
                  <IconRefresh size={18} />
                </ActionIcon>
                {teams.options.length > 1 && (
                  <Select placeholder="Every campus" data={teams.options} value={teamId} onChange={setTeamId} clearable w={220} aria-label="Campus" data-help-id={`${PAGE}.campus`} />
                )}
                {perms.canReviewIntake && (
                  <Button variant="default" leftSection={<IconDatabaseImport size={16} />} onClick={() => navigate(salvationPaths.intake(undefined, true))} data-help-id={`${PAGE}.bring`}>
                    Bring in decisions
                  </Button>
                )}
                {perms.canManagePeople && (
                  <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)} data-help-id={`${PAGE}.add`}>
                    Add a person
                  </Button>
                )}
              </>
            }
          />

          {error && <Alert color="red">{salvationErrorMessage(error, "The overview couldn't be loaded.")}</Alert>}

          {isLoading ? (
            <Stack gap="md">
              <StatTilesSkeleton count={6} />
              <StatTilesSkeleton count={5} />
              <Skeleton height={300} radius="md" />
            </Stack>
          ) : data && t ? (
            <>
              <SimpleGrid cols={{ base: 2, sm: 3, xl: 6 }} data-help-id={`${PAGE}.stats`}>
                <StatTile icon={<IconUsers size={22} />} color="blue" label="In follow-up" value={t.active} hint={`${plural(t.newThisWeek, 'new person', 'new people')} this week`} onClick={() => goPeople({ stage: '1' })} />
                <StatTile icon={<IconPhoneOff size={22} />} color="orange" label="Still to reach" value={t.stillToReach} hint="Not contacted or tried" onClick={() => goPeople({ stage: '1', sort: 'due' })} />
                <StatTile icon={<IconPhone size={22} />} color="cyan" label="Spoken to" value={t.spokenTo} hint="On the phone" onClick={() => goPeople({ statusId: String(S.Reached) })} />
                <StatTile icon={<IconBuildingChurch size={22} />} color="green" label="Met at church" value={t.met} onClick={() => goPeople({ statusId: String(S.Met) })} />
                <StatTile icon={<IconTrendingUp size={22} />} color="grape" label="Handed over" value={t.handedOver} onClick={() => goPeople({ statusId: String(S.HandedOver) })} />
                <StatTile icon={<IconHeartHandshake size={22} />} color="teal" label="Connected" value={t.connected} onClick={() => goPeople({ statusId: String(S.Connected) })} />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 2, sm: 3, lg: perms.canReviewIntake ? 5 : 4 }} data-help-id={`${PAGE}.attention`}>
                {perms.canReviewIntake && (
                  <StatTile
                    icon={<IconInbox size={22} />}
                    color="indigo"
                    label="New decisions waiting"
                    value={data.intakeWaiting ?? 0}
                    emphasis={(data.intakeWaiting ?? 0) > 0}
                    hint="To accept or reject"
                    onClick={() => navigate(salvationPaths.intake())}
                    helpId={`${PAGE}.stat-intake`}
                  />
                )}
                <StatTile icon={<IconClockExclamation size={22} />} color="red" label="Past the deadline" value={t.overdue} emphasis={t.overdue > 0} hint="Nobody reached them in time" onClick={() => goPeople({ flag: 'overdue' })} />
                <StatTile icon={<IconArrowsExchange size={22} />} color="orange" label="Backup responsible" value={t.backupResponsible} hint="The Backup steps in" onClick={() => goPeople({ flag: 'backup' })} />
                <StatTile icon={<IconEye size={22} />} color="grape" label="Needs review" value={t.reviewNeeded} emphasis={t.reviewNeeded > 0} hint="A leader should look" onClick={() => goPeople({ flag: 'review' })} />
                <StatTile icon={<IconUserQuestion size={22} />} color="gray" label="Not assigned" value={t.unassigned} emphasis={t.unassigned > 0} hint="No Lead or Backup yet" onClick={() => goPeople({ flag: 'unassigned' })} />
              </SimpleGrid>

              {t.overdue > 0 && (
                <Alert color="red" title={`${plural(t.overdue, 'person is', 'people are')} past the deadline`}>
                  The Backup is now responsible for each one. Open the list to follow up.
                </Alert>
              )}

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <Stack gap="lg">
                    <SectionCard icon={<IconHeart size={18} />} color="green" title="Follow-up progress" description={progressLabel(t)} helpId={`${PAGE}.progress`}>
                      <Stack gap="sm">
                        <MeterBar value={t.progressPercent} color="green" height={12} />
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                          <MeterRow label="Still to reach" valueLabel={t.stillToReach} value={t.active ? (t.stillToReach * 100) / t.active : 0} color="orange" />
                          <MeterRow label="Spoken to or met" valueLabel={t.spokenTo + t.met} value={t.active ? ((t.spokenTo + t.met) * 100) / t.active : 0} color="green" />
                        </SimpleGrid>
                        <Text size="xs" c="dimmed">
                          {plural(t.newThisWeek, 'person', 'people')} added this week. {t.declined ? `${plural(t.declined, 'person', 'people')} could not be reached or declined.` : ''}
                        </Text>
                      </Stack>
                    </SectionCard>

                    <SectionCard
                      icon={<IconClock size={18} />}
                      color="orange"
                      title="Priorities"
                      description="Nobody has reached them yet. Nearest deadline first."
                      helpId={`${PAGE}.priorities`}
                      actions={
                        <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => goPeople({ stage: '1', sort: 'due' })}>
                          All of them
                        </Button>
                      }
                    >
                      {data.priorities.length === 0 ? (
                        <InlineEmpty icon={<IconHeart size={22} />} title="Everyone in follow-up has been reached" />
                      ) : (
                        <Stack gap="xs">
                          {data.priorities.map((p) => (
                            <Paper key={p.convertID} withBorder radius="md" p="sm" style={{ cursor: 'pointer' }} onClick={() => setOpenPerson(p.convertID)}>
                              <Group justify="space-between" wrap="nowrap" gap="sm">
                                <Stack gap={0} style={{ minWidth: 0 }}>
                                  <Text size="sm" fw={600} lineClamp={1}>
                                    {p.fullName}
                                  </Text>
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {[
                                      p.responsible ? `${p.responsible.displayName}, ${p.backupIsResponsible ? 'Backup' : 'Lead'}` : 'Not assigned',
                                      teams.options.length > 1 ? p.teamName : null,
                                      p.dueAt ? `due ${relative(p.dueAt)}` : null,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </Text>
                                </Stack>
                                <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
                                  <StatusBadge statusId={p.statusID} statuses={statuses} size="xs" />
                                  <PersonFlags isOverdue={p.isOverdue} backupIsResponsible={p.backupIsResponsible} isEscalated={p.isEscalated} />
                                </Stack>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </SectionCard>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 5 }}>
                  <div data-help-id={`${PAGE}.breakdown`}>
                    {breakdown.length ? (
                      <DonutChartWidget title="Where everyone is" subtitle="Everyone on the Salvation Team by status" data={breakdown} height={CHART_H + 60} showLegend showPercentages showExport={false} centerLabel={`${breakdownTotal}`} />
                    ) : (
                      <ChartPlaceholder title="Where everyone is" text="Statuses show here once people are added." />
                    )}
                  </div>
                </Grid.Col>

                {showCampusChart && (
                  <Grid.Col span={{ base: 12, lg: 7 }}>
                    <div data-help-id={`${PAGE}.campus-chart`}>
                      <BarChartWidget
                        title="Campus by campus"
                        subtitle="Still to reach, spoken to or met, and handed over"
                        data={byCampus}
                        xAxisKey="campus"
                        height={CHART_H}
                        showGrid
                        showLegend
                        showExport={false}
                        bars={[
                          { dataKey: 'toReach', name: 'Still to reach', color: 'var(--mantine-color-orange-5)' },
                          { dataKey: 'spoken', name: 'Spoken to or met', color: 'var(--mantine-color-green-6)' },
                          { dataKey: 'handedOver', name: 'Handed over', color: 'var(--mantine-color-grape-5)' },
                        ]}
                      />
                    </div>
                  </Grid.Col>
                )}

                <Grid.Col span={{ base: 12, lg: showCampusChart ? 5 : 12 }}>
                  <SectionCard
                    icon={<IconBuildingChurch size={18} />}
                    color="blue"
                    title="Campus teams"
                    description="Where each campus is this week."
                    noDivider
                    helpId={`${PAGE}.campuses`}
                    actions={
                      <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(salvationPaths.teams)}>
                        Campus teams
                      </Button>
                    }
                  >
                    {data.teams.length === 0 ? (
                      <InlineEmpty
                        icon={<IconBuildingChurch size={22} />}
                        title="No campus teams yet"
                        description="Set up a team for each campus first."
                        action={
                          <Button size="xs" variant="light" onClick={() => navigate(salvationPaths.teams)}>
                            Campus teams
                          </Button>
                        }
                      />
                    ) : (
                      <ScrollArea>
                        <Table verticalSpacing="sm" miw={420} highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Campus team</Table.Th>
                              <Table.Th ta="right">To reach</Table.Th>
                              <Table.Th ta="right">Past deadline</Table.Th>
                              <Table.Th ta="right">Handed over</Table.Th>
                              <Table.Th>Progress</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {data.teams.map((row) => (
                              <Table.Tr key={row.teamID} style={{ cursor: 'pointer' }} onClick={() => navigate(`${salvationPaths.people}?teamId=${row.teamID}&stage=1`)}>
                                <Table.Td>
                                  <Text size="sm" fw={600}>
                                    {row.teamName}
                                  </Text>
                                </Table.Td>
                                <Table.Td ta="right">{row.stats.stillToReach}</Table.Td>
                                <Table.Td ta="right">
                                  {row.stats.overdue > 0 ? (
                                    <Badge color="red" variant="light">
                                      {row.stats.overdue}
                                    </Badge>
                                  ) : (
                                    0
                                  )}
                                </Table.Td>
                                <Table.Td ta="right">{row.stats.handedOver}</Table.Td>
                                <Table.Td w={140}>
                                  <MeterBar value={row.stats.progressPercent} color="green" tooltip={progressLabel(row.stats)} />
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </SectionCard>
                </Grid.Col>
              </Grid>
              <Group gap={6}>
                <IconClock size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  The numbers refresh every minute. Click a tile to open the list behind it.
                </Text>
              </Group>
            </>
          ) : null}
        </Stack>
      </FadeIn>

      <PersonDrawer convertId={openPerson} onClose={() => setOpenPerson(null)} />
      <AddPersonModal
        opened={adding}
        onClose={() => setAdding(false)}
        defaultTeamId={teamId ? Number(teamId) : null}
        canAssign={perms.canAssign}
        onAdded={(p) => setOpenPerson(p.convertID)}
        onOpenPerson={(id) => setOpenPerson(id)}
      />
    </LayoutContainer>
  );
}
